import { findActivity, findCrime, findVenue, getTown, minigameAt, type TownId } from '@wyn/content';
import { balance } from './config/balance.ts';
import { createRng } from './rng.ts';
import type { GameEvent, PlayerState } from './player.ts';

/**
 * The notice board: three tasks a day for each player, one for each bar they spend (Focus, Vigour and
 * Nerve), so the three run side by side and each wants about one full bar. The day's three are drawn
 * from the UTC day, the player's id and their home town, so no list is stored. Only progress is kept,
 * on the row, with the day it belongs to. A new day starts afresh, on whichever read or action comes
 * first after midnight. Nobody is ticked on a schedule. Rewards are Renown only: `realm:inflation` has
 * no room for another faucet.
 */
export type BoardBar = 'focus' | 'vigour' | 'nerve';

export const BOARD_BARS: readonly BoardBar[] = ['focus', 'vigour', 'nerve'];

export interface NoticeTask {
  key: string;
  bar: BoardBar;
  name: string;
  /** Where and how. */
  hint: string;
  /** The home towns whose board it is posted on; unset means every town's. */
  towns?: readonly TownId[];
  /** What it takes: bar points spent, or wins. */
  target: number;
  unit: BoardBar | 'wins';
  /** The venue it is done at, if any. */
  venue: string | null;
  /** How far one event of the player's own action takes it. */
  count: (e: GameEvent) => number;
}

/** What a task asks for: one full bar. Nerve's is its base, which guild rank only adds to. */
const FULL: Record<BoardBar, number> = { focus: balance.bars.focus.max, vigour: balance.bars.vigour.max, nerve: balance.bars.nerve.baseMax };

const barName = (b: BoardBar) => `${b[0]!.toUpperCase()}${b.slice(1)}`;

/** A full bar spent on games finished at one venue. A game walked away from doesn't count. */
function atVenue(venueKey: string, name: string, more = ''): NoticeTask {
  const venue = findVenue(venueKey)!;
  const bar = minigameAt(venueKey)!.activities[0]!.cost.bar as BoardBar;
  return {
    key: venueKey,
    bar,
    name,
    hint: `Spend ${FULL[bar]} ${barName(bar)} on games finished at ${venue.name} in ${getTown(venue.town).name}.${more}`,
    towns: [venue.town],
    target: FULL[bar],
    unit: bar,
    venue: venueKey,
    count: (e) => {
      if (e.type !== 'minigame' || e.data?.venue !== venueKey || !e.data.engine) return 0;
      const cost = findActivity(venueKey, String(e.data.activity))?.cost;
      return cost?.bar === bar ? cost.amount : 0;
    },
  };
}

/**
 * Every task that can be posted. Venues whose first tier eats materials (the Forge, the Brewhouse, the
 * Siege Works, the Herbarium) and the Market Cross (which wants goods to sell) are left out, so a task
 * never needs anything but the bar. Locksmiths' Row is the exception: a lockpick costs little and Nerve
 * has nowhere else to go in Wynchester but crime.
 */
export const NOTICE_TASKS: readonly NoticeTask[] = [
  atVenue('dice-cellar', 'Roll at the Dice Cellar'),
  atVenue('scriptorium', 'Illuminate at the Scriptorium'),
  atVenue('the-wharf', 'Fish at the Wharf'),
  atVenue('deep-shaft', 'Dig the Deep Shaft'),
  atVenue('the-black-dog', 'Play cards at the Black Dog'),
  atVenue('logging-camp', 'Fell trees at the Logging Camp'),
  atVenue('the-lists', 'Tilt at the Lists'),
  atVenue('horse-fair', 'Break horses at the Horse Fair'),
  atVenue('the-butts', 'Shoot at the Butts'),
  atVenue('royal-chase', 'Hunt the Royal Chase'),
  atVenue('locksmiths-row', 'Pick locks on Locksmiths’ Row', ' Each lock takes a lockpick from the Chandler.'),
  {
    key: 'train',
    bar: 'vigour',
    name: 'Drill at the Training Yard',
    hint: `Spend ${FULL.vigour} Vigour training.`,
    target: FULL.vigour,
    unit: 'vigour',
    venue: null,
    count: (e) => (e.type === 'trained' ? Number(e.data?.vigour ?? 0) : 0),
  },
  {
    key: 'fights',
    bar: 'vigour',
    name: 'Win two fights',
    hint: 'Win two fights, whoever starts them. Each attack costs Vigour.',
    target: 2,
    unit: 'wins',
    venue: null,
    count: (e) => {
      if (e.type !== 'fight') return 0;
      const d = e.data ?? {};
      return (d.result === 'won' && d.role === 'attacker') || (d.result === 'lost' && d.role === 'defender') ? 1 : 0;
    },
  },
  {
    key: 'crimes',
    bar: 'nerve',
    name: 'Turn to crime',
    hint: `Spend ${FULL.nerve} Nerve on crimes, caught or not.`,
    target: FULL.nerve,
    unit: 'nerve',
    venue: null,
    count: (e) => (e.type.startsWith('crime-') ? (findCrime(String(e.data?.crime))?.nerve ?? 0) : 0),
  },
];

const taskMap = new Map(NOTICE_TASKS.map((t) => [t.key, t]));
export const findNoticeTask = (key: string): NoticeTask | undefined => taskMap.get(key);

/** Renown for each task done. */
export const BOARD_TASK_RENOWN = 20;
/** Renown for doing all three in a day, and what each day of a streak adds, up to the cap. */
export const BOARD_BONUS = { renown: 40, perStreakDay: 10, maxStreakDays: 5 } as const;

/** What finishing the board pays on the given day of a streak (the first day is 1). */
export const boardBonus = (streak: number): number => BOARD_BONUS.renown + BOARD_BONUS.perStreakDay * Math.min(Math.max(0, streak - 1), BOARD_BONUS.maxStreakDays);

/** What the row keeps: progress on the day's three, and the streak. Missing means nothing yet. */
export interface NoticeBoardState {
  /** The UTC day (YYYY-MM-DD) the progress belongs to. */
  day: string;
  /** Progress on each of the day's three, in the order drawn, capped at the target. */
  progress: number[];
  /** Days in a row with all three done, up to `lastFull`. */
  streak: number;
  /** The last day all three were done. */
  lastFull?: string;
}

const DAY_MS = 86_400_000;
export const utcDay = (now: number) => new Date(now).toISOString().slice(0, 10);
const dayBefore = (day: string) => utcDay(Date.parse(`${day}T00:00:00Z`) - DAY_MS);

/** Whether the player has a board: everyone but the residents, who have no screen to read it on. */
export const hasNoticeBoard = (p: Pick<PlayerState, 'resident'>): boolean => !p.resident;

/** The day's three, one for each bar, from the day, the player's id and their home town. */
export function noticeTasks(p: Pick<PlayerState, 'id' | 'homeTown'>, day: string): NoticeTask[] {
  const rng = createRng(`notice-board:${day}:${p.id}`);
  return BOARD_BARS.map((bar) => rng.pick(NOTICE_TASKS.filter((t) => t.bar === bar && (!t.towns || t.towns.includes(p.homeTown)))));
}

/** Today's progress: the stored progress if it is today's, otherwise none. */
function progressOn(p: Pick<PlayerState, 'board'>, day: string): number[] {
  return p.board?.day === day ? [...p.board.progress] : BOARD_BARS.map(() => 0);
}

/** The streak as it stands today: it lapses once a whole day passes without the board done. */
function streakOn(s: NoticeBoardState | undefined, day: string): number {
  return s?.lastFull === day || s?.lastFull === dayBefore(day) ? s.streak : 0;
}

/**
 * Count what the action's events do towards the day's three. Each task done pays `BOARD_TASK_RENOWN`
 * with a `notice-task` event. The third done pays the bonus, more for each day of the streak (capped),
 * with `notice-board-done`. A day the row's progress doesn't belong to starts from nothing. Called by
 * `applyProgress`, so it runs inside the action's own transaction.
 */
export function advanceNoticeBoard(player: PlayerState, events: readonly GameEvent[], now: number): { player: PlayerState; events: GameEvent[] } {
  if (!hasNoticeBoard(player)) return { player, events: [] };
  const day = utcDay(now);
  const tasks = noticeTasks(player, day);
  const progress = progressOn(player, day);
  const out: GameEvent[] = [];
  let renown = 0;
  let moved = false;
  tasks.forEach((t, i) => {
    if (progress[i]! >= t.target) return;
    const add = events.reduce((n, e) => n + t.count(e), 0);
    if (add <= 0) return;
    moved = true;
    progress[i] = Math.min(t.target, progress[i]! + add);
    if (progress[i]! < t.target) return;
    renown += BOARD_TASK_RENOWN;
    out.push({ type: 'notice-task', text: `Notice board: ${t.name}. +${BOARD_TASK_RENOWN} Renown.`, data: { task: t.key } });
  });
  if (!moved) return { player, events: [] };

  const before = player.board;
  let streak = before?.streak ?? 0;
  let lastFull = before?.lastFull;
  if (tasks.every((t, i) => progress[i]! >= t.target) && lastFull !== day) {
    streak = lastFull === dayBefore(day) ? streak + 1 : 1;
    lastFull = day;
    const bonus = boardBonus(streak);
    renown += bonus;
    const days = streak > 1 ? ` That’s ${streak} days running.` : '';
    out.push({ type: 'notice-board-done', text: `The day’s notices are all done.${days} +${bonus} Renown.`, data: { streak } });
  }
  const board: NoticeBoardState = { day, progress, streak, ...(lastFull ? { lastFull } : {}) };
  return { player: { ...player, renown: player.renown + renown, board }, events: out };
}

/** The board as a player reads it: the day's three with progress, the streak, and what finishing pays. */
export interface NoticeBoardView {
  day: string;
  /** When the day ends and a new three are drawn (the next UTC midnight). */
  endsAt: number;
  tasks: { key: string; bar: BoardBar; name: string; hint: string; unit: NoticeTask['unit']; target: number; progress: number; done: boolean; venue: string | null }[];
  /** All three done today. */
  done: boolean;
  /** Days in a row with all three done, today's included once it is. */
  streak: number;
  /** Renown for each task. */
  reward: number;
  /** What finishing today's board pays (or paid), streak included. */
  bonus: number;
}

export function noticeBoardView(p: PlayerState, now: number): NoticeBoardView | null {
  if (!hasNoticeBoard(p)) return null;
  const day = utcDay(now);
  const progress = progressOn(p, day);
  const tasks = noticeTasks(p, day).map((t, i) => ({ key: t.key, bar: t.bar, name: t.name, hint: t.hint, unit: t.unit, target: t.target, progress: progress[i]!, done: progress[i]! >= t.target, venue: t.venue }));
  const done = p.board?.lastFull === day;
  const streak = streakOn(p.board, day);
  return { day, endsAt: Date.parse(`${day}T00:00:00Z`) + DAY_MS, tasks, done, streak, reward: BOARD_TASK_RENOWN, bonus: boardBonus(done ? streak : streak + 1) };
}
